import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

// Parse "30+" -> { end: 30, suffix: "+", pad: 0 }, "03" -> { end: 3, suffix: "", pad: 2 }
function parse(target) {
  const match = String(target).match(/^([\d,]+)(.*)$/);
  if (!match) return { end: 0, suffix: "", pad: 0, ok: false };
  const [, raw, suffix] = match;
  const digits = raw.replace(/,/g, "");
  const pad = digits.length > 1 && digits.startsWith("0") ? digits.length : 0;
  return { end: parseInt(digits, 10), suffix, pad, ok: true };
}

function format(value, suffix, pad) {
  const num = pad ? String(value).padStart(pad, "0") : Number(value).toLocaleString("en-US");
  return `${num}${suffix}`;
}

// Animates a number from 0 to `target` when scrolled into view.
export default function CountUp({ target, className = "", duration = 1600 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const { end, suffix, pad, ok } = parse(target);
  const [display, setDisplay] = useState(format(0, suffix, pad));

  useEffect(() => {
    if (!inView || !ok) {
      if (!ok) setDisplay(String(target));
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(format(Math.round(eased * end), suffix, pad));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
